import type { ClientOptions, Middleware } from "openapi-fetch"
import createClient from "openapi-fetch"

import { MY_SPACE } from "@/constants/spaces"
import { AuthClientInstance } from "@/lib/services/instances"
import type { Space } from "@/types"

async function getAccessToken() {
  try {
    const res = await AuthClientInstance.getTokenSilently()
    return res
  } catch {
    return null
  }
}
/** Fetch wrapper with authentication */
async function authFetch(input: string, options: RequestInit = { headers: {} }) {
  const authToken = await getAccessToken()
  const { headers, ...restOptions } = options

  if (!authToken) {
    throw new Error("Authentication error")
  }

  const requestHeaders = new Headers()
  requestHeaders.set("Authorization", `Bearer ${authToken}`)

  // Add headers
  if (headers) {
    Object.entries(headers).forEach(([key, value]) => {
      if (value !== undefined) requestHeaders.set(key, value)
    })
  }

  // If we're sending a FormData, don't set the Content-Type header.
  // See: https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest_API/Using_FormData_Objects#sending_files_using_a_formdata_object
  if (!(options.body instanceof FormData)) {
    // Pretend we're sending JSON (this is true most of the time), we can always override this later.
    requestHeaders.set("Content-Type", "application/json;charset=UTF-8")
  }

  return fetch(input, { headers: requestHeaders, ...restOptions })
}

/** XHR wrapper with authentication */
async function authXhr(
  xhr: XMLHttpRequest,
  open: {
    method: string
    url: string | URL
    headers?: Record<string, string | undefined>
  },
  payload: Parameters<XMLHttpRequest["send"]>[0]
) {
  const authToken = await getAccessToken()

  if (!authToken) {
    throw new Error("Authentication error")
  }

  const xhrPromise = new Promise<XMLHttpRequest>(resolve => {
    xhr.open(open.method, open.url)
    xhr.setRequestHeader("Authorization", `Bearer ${authToken}`)

    // Set headers
    if (open.headers) {
      Object.entries(open.headers).forEach(([key, value]) => {
        if (value !== undefined) xhr.setRequestHeader(key, value)
      })
    }

    const { onerror, onload } = xhr

    // eslint-disable-next-line no-param-reassign
    xhr.onerror = function fn(this, e) {
      resolve(xhr)
      onerror?.bind(this)(e)
    }

    // eslint-disable-next-line no-param-reassign
    xhr.onload = function fn(this, e) {
      onload?.bind(this)(e)
      if (xhr.status === 201) {
        resolve(xhr)
      } else {
        resolve(xhr)
      }
    }

    xhr.send(payload) // Send the data
  })

  return xhrPromise
}

function getXOrganizationHeaders(spaceID: Space["id"]) {
  return spaceID !== MY_SPACE.id ? { "X-Organization": spaceID } : undefined
}

function createApiClient<T extends object>(opts: ClientOptions) {
  const vanillaClient = createClient<T>(opts)
  const rqClient = createClient<T>(opts)

  // Add an authentication middleware
  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const authToken = await getAccessToken()

      if (!authToken) throw new Error("Authentication error")

      request.headers.set("Authorization", `Bearer ${authToken}`)

      return request
    }
  }

  // Vanilla client that does not throw on error, but returns the query.error field instead.
  vanillaClient.use(authMiddleware)

  // RQ client that throws the body on error.
  rqClient.use({
    ...authMiddleware,
    async onResponse({ response }) {
      if (!response.ok) {
        // Try cloning the json: if it fails, we'll try to get the text.
        let body
        try {
          body = await response.clone().json()
        } catch {
          body = await response.clone().text()
        }
        // Else, we throw the body (which is typed in the react-query client).
        throw { ...body, response }
      }
    }
  })

  return { vanillaClient, rqClient }
}

export { authFetch, authXhr, createApiClient, getXOrganizationHeaders }
